import { P2GBox, P2GText, P2GGrid, P2GLink } from '@parcel2go/core-ui';
import { useContext, useMemo } from 'react';
import { SmartSendItemGroup } from './SmartSendGroup';
import { ExtrasIcon, ExtrasIconSrc } from '../../ExtrasHelper';
import { ExtraBase } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { BulkAssignExtrasAction } from '../../../lib/context/SmartSendContextModels';
import { ExtrasSummary, GenerateSummary, GetBaseItems } from './SmartSendGroupExtrasHelpers';
import { EventSections } from '../../../lib/events/EventLogger';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { useAnalytics, useTranslation } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { TranslationNamespaces } from '~/lib/constants';
import { ToTranslatedExtra } from '~/apps/smartsend/lib/helpers/ToUniqueExtras';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';

interface props {
  group: SmartSendItemGroup;
  isXs: boolean;
  children?: React.ReactNode;
}

export default function SmartSendGroupExtras(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const ctx = useContext(SmartSendContext);
  const { track } = useAnalytics();

  const onClick = (extra: string, action: string) => {
    track({
      type: `extra_clicked`,
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendGroup, 'extras']),
        action: action,
        extra: extra
      }
    });
  };

  const loading = useMemo(
    () => props.group.States.includes('SAVE') || props.group.States.includes('QUOTE'),
    [props.group]
  );

  const extras = useMemo<ExtrasSummary[]>(() => GenerateSummary(props.group), [props.group]);

  const onAddExtraClick = (e: ExtraBase) => {
    if (loading) {
      return;
    }

    const ids = GetBaseItems(props.group)
      .filter((x) => !!x.State?.validation?.QuoteState?.Quote?.Options[0]?.AvailableExtras.find((a) => a.Id === e.Id))
      .map((x) => x.Id);

    onClick(e.Id, 'add');

    ctx.dispatch(new BulkAssignExtrasAction(ids, 'add', [e]));
  };

  const onRemoveExtraClick = (e: ExtraBase) => {
    if (loading) {
      return;
    }

    const ids = GetBaseItems(props.group)
      .filter((x) => !!x.State?.validation?.QuoteState?.Quote?.Options[0]?.Extras.find((a) => a.Id === e.Id))
      .map((x) => x.Id);

    onClick(e.Id, 'remove');

    ctx.dispatch(new BulkAssignExtrasAction(ids, 'remove', [e]));
  };

  const currencyCode = useMemo(
    () => props.group.Items[0]?.State?.validation?.QuoteState?.Quote?.CurrencyCode,
    [props.group]
  );

  if (!extras || extras.length === 0) {
    return null;
  }

  return (
    <>
      <P2GBox p={2}>
        <div style={{ opacity: loading ? 0.3 : 1 }}>
          <P2GGrid container spacing={4} alignContent="center" alignItems="center" justify="center">
            {extras.map((x) => (
              <P2GGrid item key={x.extra.Id}>
                <P2GGrid container spacing={1} alignContent="center" alignItems="center">
                  <P2GGrid item>
                    <CdnIconProxy src={ExtrasIconSrc(x.extra)} size="xs" palette="primary">
                      {ExtrasIcon(x.extra)}
                    </CdnIconProxy>
                  </P2GGrid>
                  <P2GGrid item>
                    <P2GText variant="body2">
                      {ToTranslatedExtra(t, x.extra)}
                      {x.toAdd && x.toAdd.Count > 0 && (
                        <>
                          {' '}
                          {x.toAdd.Count > 1 && <>({x.toAdd.Count}) </>}
                          {x.toAdd.Price > 0 && toDisplayCurrency(currencyCode, x.toAdd.Price)}
                        </>
                      )}
                    </P2GText>
                  </P2GGrid>
                  {x.toAdd && (
                    <P2GGrid item>
                      <P2GLink variant="body2" onClick={() => onAddExtraClick(x.extra)}>
                        {t('ADD_EXTRA_LINK', 'Add')}
                      </P2GLink>
                    </P2GGrid>
                  )}
                  {x.canRemove && (
                    <P2GGrid item>
                      <P2GLink variant="body2" onClick={() => onRemoveExtraClick(x.extra)}>
                        {t('REMOVE_EXTRA_LINK', 'Remove')}
                      </P2GLink>
                    </P2GGrid>
                  )}
                </P2GGrid>
              </P2GGrid>
            ))}
          </P2GGrid>
        </div>
      </P2GBox>
      {props.children}
    </>
  );
}
